import type { LedgerEntry } from '@vibetime/runtime';
import { type TimelineIndex, prefixUpTo } from './dag.js';

/**
 * Snapshot of the run as of a specific checkpoint id. Everything here
 * is a projection of the prefix of ledger entries up to and including
 * the checkpoint; no network, no disk, no side effects.
 */
export interface CheckpointState {
  checkpointId: string;
  entryIndex: number;
  planTasks: PlanTaskSnapshot[];
  openToolCalls: OpenToolCall[];
  fileSnapshots: FileSnapshotRef[];
  /** USD spent so far, summed from `cost` entries. */
  costUsd: number;
  /** Most-recent modelId observed in an agent actor (or null). */
  currentModelId: string | null;
  /** Goal of the run, read from `run_start`. */
  goal: string | null;
}

export interface PlanTaskSnapshot {
  id: string;
  title: string;
  role: string;
  dependsOn: string[];
  status: 'pending' | 'ready' | 'running' | 'completed' | 'failed';
}

export interface OpenToolCall {
  callId: string;
  name: string;
  startedAtMs: number;
  /** Entry index where the call was emitted. */
  at: number;
  argsSummary?: string;
}

export interface FileSnapshotRef {
  /** Absolute or workspace-relative path as written in the ledger. */
  path: string;
  /** Git blob sha referenced by the ledger (ledger-only; blobs live in git). */
  blobSha: string;
  /** Last entry index that mentioned the path. */
  at: number;
}

interface PlanPayloadTask {
  id: string;
  title?: string;
  role: string;
  dependsOn?: string[];
}

function readPlanTasks(entry: LedgerEntry): PlanPayloadTask[] {
  const raw = (entry.payload as { tasks?: unknown }).tasks;
  if (!Array.isArray(raw)) return [];
  const out: PlanPayloadTask[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const t = r as Record<string, unknown>;
    if (typeof t.id !== 'string' || typeof t.role !== 'string') continue;
    const deps = Array.isArray(t.dependsOn)
      ? t.dependsOn.filter((d): d is string => typeof d === 'string')
      : [];
    out.push({
      id: t.id,
      title: typeof t.title === 'string' ? t.title : undefined,
      role: t.role,
      dependsOn: deps,
    });
  }
  return out;
}

function readTaskId(entry: LedgerEntry): string | null {
  const v = (entry.payload as { taskId?: unknown }).taskId;
  return typeof v === 'string' ? v : null;
}

function readCallId(entry: LedgerEntry): string | null {
  const v = (entry.payload as { callId?: unknown; id?: unknown }).callId;
  if (typeof v === 'string') return v;
  const alt = (entry.payload as { id?: unknown }).id;
  return typeof alt === 'string' ? alt : null;
}

function readGoal(entries: LedgerEntry[]): string | null {
  const start = entries.find((e) => e.kind === 'run_start');
  const goal = (start?.payload as { goal?: unknown } | undefined)?.goal;
  return typeof goal === 'string' ? goal : null;
}

export function stateAt(index: TimelineIndex, checkpointId: string): CheckpointState {
  const prefix = prefixUpTo(index, checkpointId);
  const node = index.byId.get(checkpointId);

  const tasksById = new Map<string, PlanTaskSnapshot>();
  const openCalls = new Map<string, OpenToolCall>();
  const files = new Map<string, FileSnapshotRef>();
  let costUsd = 0;
  let currentModelId: string | null = null;

  for (const entry of prefix) {
    if (entry.actor.modelId) currentModelId = entry.actor.modelId;

    switch (entry.kind) {
      case 'plan': {
        const tasks = readPlanTasks(entry);
        for (const t of tasks) {
          const prev = tasksById.get(t.id);
          tasksById.set(t.id, {
            id: t.id,
            title: t.title ?? prev?.title ?? t.id,
            role: t.role,
            dependsOn: t.dependsOn ?? [],
            status: prev?.status ?? 'pending',
          });
        }
        break;
      }
      case 'agent_start': {
        const taskId = readTaskId(entry);
        if (taskId) {
          const prev = tasksById.get(taskId);
          tasksById.set(taskId, {
            id: taskId,
            title: prev?.title ?? taskId,
            role: prev?.role ?? entry.actor.role,
            dependsOn: prev?.dependsOn ?? [],
            status: 'running',
          });
        }
        break;
      }
      case 'agent_end': {
        const taskId = readTaskId(entry);
        if (taskId) {
          const prev = tasksById.get(taskId);
          const outcome = (entry.payload as { status?: unknown }).status;
          const status = outcome === 'failed' ? 'failed' : 'completed';
          tasksById.set(taskId, {
            id: taskId,
            title: prev?.title ?? taskId,
            role: prev?.role ?? entry.actor.role,
            dependsOn: prev?.dependsOn ?? [],
            status,
          });
        }
        break;
      }
      case 'tool_call': {
        const callId = readCallId(entry);
        if (callId) {
          const name = String((entry.payload as { name?: unknown }).name ?? 'tool');
          const argsRaw = (entry.payload as { args?: unknown }).args;
          const argsSummary = argsRaw ? summarize(argsRaw) : undefined;
          openCalls.set(callId, {
            callId,
            name,
            startedAtMs: entry.timestamp,
            at: prefix.length - 1,
            argsSummary,
          });
        }
        break;
      }
      case 'tool_result': {
        const callId = readCallId(entry);
        if (callId) openCalls.delete(callId);
        const path = readString(entry.payload, 'path');
        const blob = readString(entry.payload, 'blobSha') ?? readString(entry.payload, 'sha');
        if (path && blob) {
          files.set(path, { path, blobSha: blob, at: prefix.length - 1 });
        }
        break;
      }
      case 'diff_applied':
      case 'diff_proposed': {
        const path = readString(entry.payload, 'path');
        const blob = readString(entry.payload, 'blobSha') ?? readString(entry.payload, 'sha');
        if (path && blob) {
          files.set(path, { path, blobSha: blob, at: prefix.length - 1 });
        }
        break;
      }
      case 'cost': {
        const n = Number((entry.payload as { costUsd?: unknown }).costUsd ?? 0);
        if (Number.isFinite(n)) costUsd += n;
        break;
      }
    }
  }

  return {
    checkpointId,
    entryIndex: node?.index ?? -1,
    planTasks: sortTasks([...tasksById.values()]),
    openToolCalls: [...openCalls.values()].sort((a, b) => a.startedAtMs - b.startedAtMs),
    fileSnapshots: [...files.values()].sort((a, b) => a.path.localeCompare(b.path)),
    costUsd: round6(costUsd),
    currentModelId,
    goal: readGoal(prefix),
  };
}

function sortTasks(tasks: PlanTaskSnapshot[]): PlanTaskSnapshot[] {
  return tasks.slice().sort((a, b) => a.id.localeCompare(b.id));
}

function readString(payload: Record<string, unknown>, key: string): string | null {
  const v = payload[key];
  return typeof v === 'string' ? v : null;
}

function summarize(value: unknown): string {
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  return text.length > 140 ? `${text.slice(0, 137)}...` : text;
}

function round6(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}
